import { queryOptions } from '@tanstack/react-query';
import { getApartmentPricing, getPricingStrategy, listPricingStrategies, previewPricing } from '@/api/generated/sdk.gen';
import type { ApartmentPricing, PreviewPricingCell, PricingRule, PricingStrategy } from '@/api/generated/types.gen';

export type { ApartmentPricing, PreviewPricingCell, PricingRule, PricingStrategy };

// The 10 v1 rule types (mirrors PricingRule.type in the OpenAPI spec / ADR-038).
export type PricingRuleType = PricingRule['type'];

// Human labels for rule types, shown in the strategy editor.
export const RULE_TYPE_LABELS: Record<PricingRuleType, string> = {
  month: 'Month adjustment',
  seasonal: 'Seasonal adjustment',
  day_of_week: 'Day-of-week adjustment',
  date_specific: 'Date-specific',
  min_nights: 'Minimum nights (by weekday)',
  booking_restriction: 'Booking restriction',
  gap_night: 'Gap-night',
  last_minute: 'Last-minute',
  advanced_booking: 'Advanced booking',
  extra_guest: 'Extra guest (adults over base)',
};

// apartmentPricingQuery — the apartment's anchors + assigned strategy.
//
// An apartment with no settings row yet returns 404 ("No pricing settings
// found"). That is a normal "unconfigured" state, not an error, so the
// queryFn maps 404 → null and the section renders a configure prompt. Any
// other non-2xx still rejects so the error UI shows.
export const apartmentPricingQuery = (apartmentId: string) =>
  queryOptions({
    queryKey: ['apartment-pricing', apartmentId],
    queryFn: async ({ signal }): Promise<ApartmentPricing | null> => {
      try {
        const res = await getApartmentPricing({
          throwOnError: true,
          signal,
          path: { apartmentId },
        });
        return res.data;
      } catch (err) {
        const status = (err as { status?: number })?.status;
        if (status === 404) return null;
        throw err;
      }
    },
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && status !== 404 && failureCount < 1;
    },
    staleTime: 30_000,
  });

// pricingStrategiesQuery — the list of reusable strategies, used to populate
// the apartment's strategy dropdown and the strategies editor.
export const pricingStrategiesQuery = queryOptions({
  queryKey: ['pricing-strategies'],
  queryFn: async ({ signal }): Promise<PricingStrategy[]> => {
    const res = await listPricingStrategies({ throwOnError: true, signal });
    return res.data.items;
  },
  retry: (failureCount, err) => {
    const status = (err as { status?: number })?.status;
    return status !== 401 && failureCount < 1;
  },
  staleTime: 60_000,
});

// previewPricingQuery — computed strategy cells for an apartment over
// [dateFrom, dateTo] (the calendar's "strategy preview" overlay, roadmap §1r
// slice 5d). Like apartmentPricingQuery, an apartment with no pricing
// settings yet returns 404 ("No pricing settings found") — that means
// "unmanaged, no overlay to show" rather than an error, so the queryFn maps
// it to an empty cell list.
//
// Gap-night caveat: the v1 preview endpoint passes no confirmed booking
// intervals, so gap_night rules never fire here — only month, seasonal,
// day_of_week, date_specific, min_nights, booking_restriction, last_minute
// and advanced_booking are reflected in the previewed rate.
export const previewPricingQuery = (apartmentId: string, dateFrom: string, dateTo: string) =>
  queryOptions({
    queryKey: ['pricing-preview', apartmentId, dateFrom, dateTo] as const,
    queryFn: async ({ signal }): Promise<PreviewPricingCell[]> => {
      try {
        const res = await previewPricing({
          throwOnError: true,
          signal,
          body: { apartment_id: apartmentId, date_from: dateFrom, date_to: dateTo },
        });
        return res.data.cells;
      } catch (err) {
        const status = (err as { status?: number })?.status;
        if (status === 404) return [];
        throw err;
      }
    },
    enabled: Boolean(apartmentId) && Boolean(dateFrom) && Boolean(dateTo),
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && status !== 404 && failureCount < 1;
    },
    staleTime: 30_000,
  });

// strategyQuery — a single strategy with its full rules array (the editor).
export const strategyQuery = (id: string) =>
  queryOptions({
    queryKey: ['pricing-strategies', id],
    queryFn: async ({ signal }): Promise<PricingStrategy> => {
      const res = await getPricingStrategy({ throwOnError: true, signal, path: { id } });
      return res.data;
    },
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && status !== 404 && failureCount < 1;
    },
    staleTime: 30_000,
  });
